import { useMemo } from 'react';
import { Col, Row, Card, Button } from 'react-bootstrap';
import { useQuery, useMutation, NetworkStatus } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { validate as uuidValidate } from 'uuid';

import LinkContainer from '../components/link_container';
import { renderOverlay, renderOffline, renderError } from '../components/render_helpers';
import ReactTable from '../components/react_table/react_table';
import { toastSuccess, toastWarning, toastError } from '../lib/toast_helpers';
import {
  picklistListPageQuery,
  picklistDelete as picklistDeleteMutation,
} from '../graphql/picklist_queries';
import { settingsSet } from '../store/settings_slice';
import * as updateFunctions from '../update_functions';

const PicklistList = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const settingsMutating = useSelector((state) => state.settings.mutating);
  const settingsOnline = useSelector((state) => state.settings.online);
  const settingsTenant = useSelector((state) => state.settings.tenant);
  const [picklistDelete] = useMutation(picklistDeleteMutation);

  const {
    data: pageData,
    loading: pageLoading,
    error: pageError,
    refetch: pageRefetch,
    networkStatus: pageNetworkStatus,
    client: pageClient,
  } = useQuery(picklistListPageQuery, {
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ picklistList }) => {
      // write an individual query for every picklist in the list
      updateFunctions.picklistList(pageClient, picklistList);
    },
  });

  const pageLoadedOrRefetching = useMemo(
    () => !pageLoading || (pageLoading && pageNetworkStatus === NetworkStatus.refetch),
    [pageLoading, pageNetworkStatus]
  );

  const picklistDeleteClicked = async (e) => {
    const dataId = e.currentTarget.getAttribute('data-id');
    const picklistId = uuidValidate(dataId)
      ? dataId
      : parseInt(e.currentTarget.getAttribute('data-id'), 10);
    const mutationData = {
      variables: { id: picklistId },
      context: {
        serializationKey: settingsTenant,
        tracked: true,
        recordType: 'PicklistType',
        recordId: picklistId,
        mutationType: 'DELETE',
      },
      update: updateFunctions.picklistDelete,
    };
    mutationData.optimisticResponse = updateFunctions.optimisticNew({
      mutationName: 'picklistDelete',
      mutationData,
    });
    dispatch(
      settingsSet({
        mutating: true,
      })
    );
    if (settingsOnline) {
      try {
        await picklistDelete(mutationData);
        toastSuccess('Picklist delete ok');
      } catch (err) {
        console.log(err.toString());
        toastError('Picklist delete failed');
      } finally {
        dispatch(
          settingsSet({
            mutating: false,
          })
        );
      }
    } else {
      picklistDelete(mutationData);
      toastWarning(`Picklist delete ok locally. Go online to make permanent`);
      dispatch(
        settingsSet({
          mutating: false,
        })
      );
    }
  };

  const renderPicklistProductsCell = (cell) => {
    const items = cell.getValue();
    return (
      <span>
        {items.map((item) => {
          const {
            id,
            quantity,
            product: { name },
          } = item;
          return (
            <span key={id}>
              {`${quantity} X ${name}`}
              <br />
            </span>
          );
        })}
      </span>
    );
  };

  const getPicklistTemplateName = (row) => {
    const {
      picklistTemplate: { name: picklistTemplateName },
    } = row;
    return picklistTemplateName;
  };

  const getToSiteLocationName = (row) => {
    const {
      toSiteLocation: { name: toSiteLocationName },
    } = row;
    return toSiteLocationName;
  };

  const getFromSiteLocationName = (row) => {
    const {
      fromSiteLocation: { name: fromSiteLocationName },
    } = row;
    return fromSiteLocationName;
  };

  const parentColumns = [
    {
      header: 'Template Name',
      accessorKey: 'name',
      accessorFn: (row) => getPicklistTemplateName(row),
      filterFn: 'equalsString',
      filterType: 'dropdown',
    },
    {
      header: 'Pick From',
      id: 'fromSiteLocation',
      accessorFn: (row) => getFromSiteLocationName(row),
      filterFn: 'equalsString',
      filterType: 'dropdown',
    },
    {
      header: 'Pick To',
      id: 'toSiteLocation',
      accessorFn: (row) => getToSiteLocationName(row),
      filterFn: 'equalsString',
      filterType: 'dropdown',
    },
    {
      header: 'Product Assemblies',
      accessorKey: 'picklistProducts',
      cell: renderPicklistProductsCell,
      enableColumnFilter: true,
      filterFn: 'includesString',
    },
  ];

  const renderContent = () => (
    <>
      <Row className="mt-4 mb-3">
        <Col sm={12}>
          <div className="float-none">
            <div className="float-start">
              <h1 className="h3 mb-3">Picklists</h1>
            </div>
            <div className="float-end">
              <Button
                className="me-2"
                variant="primary"
                onClick={() => pageRefetch()}
                disabled={!settingsOnline}
              >
                Refresh
              </Button>
              <LinkContainer to="/picklists/new">
                <Button variant="primary">New Picklist</Button>
              </LinkContainer>
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <Card.Body>
              <ReactTable
                rootName="picklist"
                parentColumns={parentColumns}
                data={pageData.picklistList}
                // doShow={false}
                doDelete={currentUser?.perms?.administrator}
                handleDelete={picklistDeleteClicked}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  return (
    <div>
      {renderOverlay(pageLoading, settingsMutating, settingsOnline)}
      {renderOffline(settingsOnline)}
      {renderError(pageError)}
      {!pageError && pageLoadedOrRefetching && renderContent()}
    </div>
  );
};

export default PicklistList;
